declare let API_HREF: string;
// Dependencies
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
// Core & Lab
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import LinearProgress from '@mui/material/LinearProgress';
import TablePagination from '@mui/material/TablePagination';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import RestoreFromTrash from '@mui/icons-material/RestoreFromTrash';
import Tooltip from '@mui/material/Tooltip';
// Components
import PictureModalSearch from './pictureModalSearch';
import PictureModalListActions from './pictureModalListActions';
import PictureCategoryLabel from './pictureCategoryLabel';
// Actions
import { setList, setPage, setLimit, setRunning, toggleRefresh, setFilterIsActive } from './redux/actions';
// Types
import { AppState } from '../../reducers';
import { Picture } from './objects/picture';
import localeText from '../../functions/localeText';
import { Close } from '@mui/icons-material';
import { AppBar, Toolbar } from '@mui/material';
import { setFinished } from '../utils/finished/redux/actions';
import { useLocation } from 'react-router-dom';

type Props = {
    onClickPicture?: (picture: Picture) => void,
    actions?: (picture: Picture) => JSX.Element | null,
    filter?: boolean
}

let request_timeout: NodeJS.Timeout;

const PictureModalList: FC<Props> = (props): ReactElement => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const [pictureToDelete, setPictureToDelete] = useState<Picture | null>(null);
    const [pictureToRestore, setPictureToRestore] = useState<Picture | null>(null);
    const medium = useMediaQuery(theme.breakpoints.down('md'));
    const large = useMediaQuery(theme.breakpoints.down('lg'));
    const extra_large = useMediaQuery(theme.breakpoints.down('xl'));
    const client_id = useSelector((state: AppState) => state.header.client_id);
    const count = useSelector((state: AppState) => state.picture.count);
    const filter_types = useSelector((state: AppState) => state.picture.filter_types);
    const filter_is_active = useSelector((state: AppState) => state.picture.filter_is_active);
    const list = useSelector((state: AppState) => state.picture.list);
    const limit = useSelector((state: AppState) => state.picture.limit);
    const page = useSelector((state: AppState) => state.picture.page);
    const search = useSelector((state: AppState) => state.picture.search);
    const running = useSelector((state: AppState) => state.picture.running);
    const refresh = useSelector((state: AppState) => state.picture.refresh);
    const manual_accommodation = useSelector((state: AppState) => state.manualAccommodation.manual_accommodation);
    const main_locale = useSelector((state: AppState) => state.locale.main_locale);
    const location = useLocation();

    const onLimitChange = (event: { target: { value: any; }; }) => {
        dispatch(setLimit(event.target.value));
    };
    const onChangePage = (event: any, page: any) => {
        dispatch(setPage(page));
    };
    const onClickPicture = (picture: Picture) => {
        if (props.onClickPicture) {
            props.onClickPicture(picture);
        }
    };

    const onConfirmDelete = async () => {
        if (!pictureToDelete || !client_id) return;
        try {
            dispatch(setFinished(true));
            const request = new FormData();
            request.append('is_active', 'false');
            await axios({
                method: 'PATCH',
                url: `${API_HREF}client/${client_id}/pictures/${pictureToDelete.id}/`,
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
                data: request
            });
            dispatch(toggleRefresh());
        } catch (error) {
            console.log('error:', error);
        } finally {
            setPictureToDelete(null);
            dispatch(setFinished(false));
        }
    };

    const onConfirmRestore = async () => {
        if (!pictureToRestore || !client_id) return;
        try {
            dispatch(setFinished(true));
            const request = new FormData();
            request.append('is_active', 'true');
            await axios({
                method: 'PATCH',
                url: `${API_HREF}client/${client_id}/pictures/${pictureToRestore.id}/`,
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
                data: request
            });
            dispatch(toggleRefresh());
        } catch (error) {
            console.log('error:', error);
        } finally {
            setPictureToRestore(null);
            dispatch(setFinished(false));
        }
    };

    const runRequest = () => {
        axios({
            method: 'GET',
            url: `${API_HREF}client/${client_id}/pictures/`,
            headers: {
                'Authorization': `Token ${localStorage.getItem('token')}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Accept-Language': 'fr'
            },
            params: {
                limit: limit,
                offset: limit * page,
                search: search !== '' ? search : undefined,
                type__in: props.filter ?
                    filter_types.join(',') :
                    (manual_accommodation !== null ? 'HOTEL' : 'PICTURE'),
                ordering: '-created_date',
                is_active: filter_is_active
            }
        }).then((response) => {
            dispatch(setList(response.data));
        }).catch((error) => {
            console.log('error : ', error);
            dispatch(setRunning(false));
        });
    };
    useEffect(() => {
        dispatch(setRunning(true));
        clearTimeout(request_timeout);
        request_timeout = setTimeout(runRequest, 500);
    }, [limit, page, search, filter_types, filter_is_active, refresh]);

    useEffect(() => {
        dispatch(setFilterIsActive(true));
    }, [location.pathname]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <PictureModalSearch />
                </Grid>
                {running && <Grid item xs={12}><LinearProgress /></Grid>}
                {
                    list !== null && list.map((picture, picture_index) => (
                        <Grid key={`picture-modal-picture-${picture_index}`} item xs={medium ? 12 : large ? 6 : extra_large ? 4 : 3}>
                            <Card
                                sx={{
                                    cursor: props.onClickPicture ? 'pointer' : 'inherit',
                                    '&:hover .pm-media': { transform: 'scale(1.08)', filter: !picture.is_active ? 'blur(3px) grayscale(70%)' : 'blur(3px)' },
                                    '&:hover .pm-overlay': { opacity: 1 },
                                }}
                            >
                                <Box sx={{ position: 'relative', overflow: 'hidden', height: 200 }}>
                                    <CardMedia
                                        className="pm-media"
                                        component={'img'}
                                        alt={''}
                                        height={200}
                                        image={picture?.thumbnail_little ?? picture?.thumbnail_medium ?? picture?.thumbnail_big ?? picture?.url ?? ''}
                                        sx={{
                                            transition: 'transform 0.35s ease, filter 0.35s ease',
                                            display: 'block',
                                            filter: !picture.is_active ? 'grayscale(70%)' : undefined,
                                        }}
                                    />
                                    <Box
                                        className="pm-overlay"
                                        sx={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1.5,
                                            opacity: 0,
                                            transition: 'opacity 0.3s ease',
                                            backgroundColor: 'rgba(0,0,0,0.35)',
                                        }}
                                    >
                                        {picture.is_active === false ? (
                                            <Tooltip title={t('shared.restore-item')} placement="top">
                                                <IconButton
                                                    onClick={(e) => { e.stopPropagation(); setPictureToRestore(picture); }}
                                                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                                                >
                                                    <RestoreFromTrash />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <>
                                                {props.onClickPicture && (
                                                    <IconButton
                                                        onClick={(e) => { e.stopPropagation(); onClickPicture(picture); }}
                                                        sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                                                    >
                                                        <EditOutlined />
                                                    </IconButton>
                                                )}
                                                <IconButton
                                                    onClick={(e) => { e.stopPropagation(); setPictureToDelete(picture); }}
                                                    sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                                                >
                                                    <DeleteOutlined />
                                                </IconButton>
                                            </>
                                        )}
                                    </Box>
                                </Box>
                                <CardContent>
                                    <Typography>{localeText(main_locale, picture.localization ?? [], picture.name ?? '-', 'name')}</Typography>
                                    <PictureCategoryLabel picture={picture} />
                                </CardContent>
                                <CardActions>
                                    {
                                        props.actions ?
                                            props.actions(picture) :
                                            <PictureModalListActions picture={picture} />
                                    }
                                </CardActions>
                            </Card>
                        </Grid>
                    ))
                }
            </Grid>
            <TablePagination rowsPerPageOptions={[12, 24, 36, 48]} component={'div'} count={count} rowsPerPage={limit} page={page} onPageChange={onChangePage} onRowsPerPageChange={onLimitChange} />
            <Dialog open={!!pictureToDelete} maxWidth="sm" fullWidth onClose={() => setPictureToDelete(null)}>
                <AppBar className={'ft-user-createdMax-appBar'}>
                    <Toolbar>
                        <IconButton edge={'start'} color={'inherit'} onClick={() => setPictureToDelete(null)}><Close /></IconButton>
                        <Typography variant={'h6'}>{t('shared.delete-picture')}</Typography>
                    </Toolbar>
                </AppBar>
                <DialogContent>
                    <DialogContentText>{t('shared.delete-picture-confirm')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPictureToDelete(null)}>{t('shared.cancel')}</Button>
                    <Button onClick={onConfirmDelete} color="error" variant="outlined">{t('shared.delete')}</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={!!pictureToRestore} maxWidth="sm" fullWidth onClose={() => setPictureToRestore(null)}>
                <AppBar className={'ft-user-appBar'}>
                    <Toolbar>
                        <IconButton edge={'start'} color={'inherit'} onClick={() => setPictureToRestore(null)}><Close /></IconButton>
                        <Typography variant={'h6'}>{t('shared.restore-item')}</Typography>
                    </Toolbar>
                </AppBar>
                <DialogContent>
                    <DialogContentText>{t('shared.restore-picture-confirm')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPictureToRestore(null)}>{t('shared.cancel')}</Button>
                    <Button onClick={onConfirmRestore} variant="outlined">{t('shared.restore-item')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PictureModalList;